import { BASE, DEFAULT_DECIMAL_DISPLAY_POSITION, DEFAULT_INDEX_DECIMALS } from 'app-constants'
import Big from 'big.js'
import { AssetDataWithPrices } from '../services/query-v2/products/index/product-info/model'

type NullableBigSource = number | string | Big | undefined | null

declare module 'big.js' {
  interface Big {
    toBigInt(): bigint
    toFixedSpecial(n: number): string
    toFormat(n: Big, precision: number): string
    precision(sd: number): Big
    customMul(n: NullableBigSource): Big
  }
}

Big.RM = Big.roundDown //Round towards zero, truncate without rounding.
Big.NE = -18
Big.PE = 40

// TODO:
// Big.prototype.customMul = function (n: NullableBigSource) {
//   if (n === undefined || n === null) {
//     console.log('mul error, number: ', n)
//   } else {
//     return this.Big.mul(n)
//   }
// }

Big.prototype.toBigInt = function () {
  return BigInt(this.toFixed(0))
}
Big.prototype.toFixedSpecial = function (n: number) {
  let str = this.toFixed(n)
  if (str === '0.00') {
    return '0'
  }

  if (str.indexOf('e+') === -1) {
    return str
  }

  // if number is in scientific notation, pick (b)ase and (p)ower
  str = str
    .replace('.', '')
    .split('e+')
    .reduce(function (p: string, b: number) {
      return p + Array(b - p.length + 2).join('0')
    })

  if (n > 0) {
    str += '.' + Array(n + 1).join('0')
  }
  return str
}

Big.prototype.toFormat = (n: Big, precision: number) => {
  const newNumber = Big(n.toFixed(precision)).toFixed()
  if (newNumber == '0' && !n.eq(0)) {
    return '< 0.000001'
  }
  return newNumber
}

Big.prototype.precision = function (n: number) {
  const integer = Big(this).toFixed(0)
  const fixed = Big(Big(this).toFixedSpecial(n))
  let number = n + integer.length
  if (number > 0 && fixed.c.length > number) {
    while (fixed.c[number - 1] === 0 && number > 1) --number
    fixed.c.length = number
  }
  return fixed
}

export const valueToPercent = (value: string, balance: string): number => {
  const valueBN = Big(value)
  const balanceBN = Big(balance)
  const percentage = Big(100)
  if (value.length > 0 && balanceBN.gt(0)) {
    assertMultiply(percentage, valueBN.div(balanceBN))
    return percentage.mul(valueBN.div(balanceBN)).round().toNumber()
  } else {
    return 0
  }
}

export const fromBNValue = (value: Big | string, providedDecimals?: string | number): Big => {
  const decimals = providedDecimals ? Number(providedDecimals) : DEFAULT_INDEX_DECIMALS
  return Big(value).div(Big(BASE).pow(decimals))
}

export const formatWeiToAmount = (value: bigint | string, decimals: number): Big => {
  return Big(value.toString()).div(Big(BASE).pow(decimals))
}

export const convertAmountToWei = (value: Big, decimals: number): Big => {
  assertMultiply(value, BASE)
  return value.mul(Big(BASE).pow(decimals)).round()
}

const formatValue = (value: string, endDecimals: number) => {
  return `${value.substring(0, 3 - endDecimals)}.${value.substring(
    3 - endDecimals,
    6 - endDecimals,
  )}`
}

export const marketCapNumber = (value: string, symbol?: string): string => {
  return `${symbol ?? ''}${shortValue(value)}`
}

export const shortValue = (bigValue: string): string => {
  const value = Big(bigValue).toFixed(DEFAULT_DECIMAL_DISPLAY_POSITION)
  switch (true) {
    case value.length <= 6:
      return `${value === '0.00' ? '<0.01' : value}`
    case value.length > 6 && value.length <= 9:
      return `${Big(formatValue(value, 9 - value.length)).toFixed(2)}K`
    case value.length > 9 && value.length <= 12:
      return `${Big(formatValue(value, 12 - value.length)).toFixed(2)}M`
    case value.length > 12 && value.length <= 15:
      return `${Big(formatValue(value, 15 - value.length)).toFixed(2)}B`
    case value.length > 15 && value.length <= 18:
      return `${Big(formatValue(value, 18 - value.length)).toFixed(2)}T`
    case value.length > 18 && value.length <= 21:
      return `${Big(formatValue(value, 21 - value.length)).toFixed(2)}P`
    case value.length > 21 && value.length <= 24:
      return `${Big(formatValue(value, 24 - value.length)).toFixed(2)}E`
    default:
      return `${value}`
  }
}

export const getAssetQuantityShortened = (asset: AssetDataWithPrices): string => {
  return marketCapNumber(Big(asset.balance).toFixed(4))
}

export const assertMultiply = (n1: NullableBigSource, n2: NullableBigSource) => {
  if (n1 === undefined || n1 === null || n2 === undefined || n2 === null) {
    console.log('n1: ', n1, 'n2: ', n2)
  }
}
